import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ProgressService } from '../../services/progress.service';
import { SharedModule } from 'src/app/shared/shared.module';

interface StepItem {
  number: string;
  label: string;
  route: string;
  icon: string;
}

@Component({
  selector: 'app-onboarding-stepper',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, SharedModule],
  templateUrl: './onboarding-stepper.component.html',
  styleUrls: ['./onboarding-stepper.component.scss'],
})
export class OnboardingStepperComponent implements OnInit, OnDestroy {
  protected readonly router = inject(Router);
  protected readonly progressService = inject(ProgressService);
  protected readonly destroy$ = new Subject<void>();

  protected readonly steps: StepItem[] = [
    { number: '01', label: 'Create Account', route: 'create-account', icon: 'user-plus' },
    { number: '02', label: 'Club Details', route: 'club-details', icon: 'club-details' },
    { number: '03', label: 'Review & Submit', route: 'review-submit', icon: 'eye' },
  ];

  protected readonly currentRoute = signal<string>('');

  ngOnInit(): void {
    this.updateCurrentRoute();
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => this.updateCurrentRoute());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateCurrentRoute(): void {
    const url = this.router.url;
    if (url.includes('create-account')) this.currentRoute.set('create-account');
    else if (url.includes('club-details')) this.currentRoute.set('club-details');
    else if (url.includes('review-submit')) this.currentRoute.set('review-submit');
    else if (url.includes('welcome')) this.currentRoute.set('welcome');
  }

  protected isActive(step: StepItem): boolean {
    return this.currentRoute() === step.route;
  }

  protected isCompleted(step: StepItem): boolean {
    const stepIndex = this.steps.indexOf(step);
    const currentIndex = this.steps.findIndex(s => s.route === this.currentRoute());
    if (currentIndex > stepIndex) return true;
    switch (step.route) {
      case 'create-account': return this.progressService.isStep1Completed();
      case 'club-details': return this.progressService.isStep2Completed();
      default: return false;
    }
  }

  protected isConnectorCompleted(step: StepItem): boolean {
    return this.isCompleted(step);
  }
}
